import {
  Box,
  Checkbox,
  FieldErrorMessage,
  Input,
  Label,
  Radio,
  Select,
  Text,
  ToggleSwitch,
} from "@vinomofo/components";
import {
  ErrorMessage,
  ErrorMessageProps,
  FieldProps,
  FormikErrors,
} from "formik";
import { Children, isValidElement, cloneElement, PropsWithChildren } from "react";

import InputMask from "react-input-mask";
import styled from "styled-components";

const OptionalLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.text.normal};
  font-size: 11px;
  text-transform: lowercase;
  font-weight: normal;
  margin-left: 2px;
  font-family: "Graphik", sans-serif;
`;

const FormErrorMessage = ({
  children,
  ...rest
}: ErrorMessageProps & { children: string }) => {
  return <FieldErrorMessage message={children} {...rest} />;
};

interface SharedInputProps {
  field: string;
  value: string;
  label?: string;
  hint?: string;
}

interface TextFieldProps extends SharedInputProps {
  mask?: string;
  isRequired?: boolean;
  maxLength?: number;
}

export const TextField = ({
  field,
  label,
  hint,
  isRequired = true,
  mask,
  form,
  maxLength,
  ...rest
}: FieldProps & TextFieldProps): JSX.Element => {
  const hasError = field.name in form.errors && field.name in form.touched;

  return (
    <Box>
      {label && (
        <Box mb={1}>
          <Label htmlFor={field.name}>
            {label}{" "}
            {!isRequired && (
              <OptionalLabel as="span" fontFamily="heading">
                optional
              </OptionalLabel>
            )}
            {maxLength && (
              <OptionalLabel as="span" fontFamily="heading">
                (maximum of {maxLength} characters allowed)
              </OptionalLabel>
            )}
          </Label>
        </Box>
      )}
      {mask ? (
        <InputMask mask={mask} {...field} {...rest} maskPlaceholder={null}>
          <Input />
        </InputMask>
      ) : (
        <Input
          {...field}
          {...rest}
          maxLength={maxLength ?? 524288} // 524288 is the max characters allowed in an input
        />
      )}
      {hint && <Text fontSize="12px">{hint}</Text>}
      {/* Fixed height here stops the input jumping if there's an error */}
      <Box minHeight="20px" paddingTop="5px" mb="6px">
        {hasError && (
          <FieldErrorMessage message={`${form.errors[field.name]}`} />
        )}
      </Box>
    </Box>
  );
};
interface SelectFieldProps extends SharedInputProps {
  isRequired?: boolean;
}

export const SelectField = ({
  field,
  label,
  hint,
  isRequired = true,
  form,
  ...rest
}: FieldProps & SelectFieldProps): JSX.Element => {
  const hasError = field.name in form.errors;

  return (
    <Box>
      {label && (
        <Box mb={1}>
          <Label>
            {label}{" "}
            {!isRequired && (
              <OptionalLabel as="span" fontFamily="heading">
                optional
              </OptionalLabel>
            )}
          </Label>
        </Box>
      )}

      <Select {...field} {...rest} />

      {hint && <Text fontSize="12px">{hint}</Text>}

      {/* Fixed height here stops the input jumping if there's an error */}
      <Box minHeight="20px" paddingTop="5px" mb="6px">
        {hasError && (
          <FieldErrorMessage message={`${form.errors[field.name]}`} />
        )}
      </Box>
    </Box>
  );
};

export const ToggleSwitchField = ({
  field,
  ...rest
}: FieldProps): JSX.Element => {
  return <ToggleSwitch {...field} {...rest} />;
};

interface CheckboxFieldProps extends SharedInputProps {
  hideErrors?: boolean;
  withCustomLabel?: boolean;
}

export const CheckboxField = ({
  field,
  label,
  hint,
  form,
  hideErrors,
  withCustomLabel = true,
  ...rest
}: FieldProps & CheckboxFieldProps): JSX.Element => {
  const hasError = field.name in form.errors && !hideErrors;

  return (
    <Box>
      <Box display="flex" alignItems="baseline">
        <Box mr={[20, 50, 50]}>
          <Checkbox label={label} {...field} {...rest} />
        </Box>
        {label && withCustomLabel && (
          <Box mb={1}>
            <Text color="heading" fontWeight={500}>
              {label}
            </Text>
            {hint && <Text variant="sm">{hint}</Text>}
          </Box>
        )}
      </Box>

      <Box minHeight="20px" paddingTop="5px" mb="6px">
        {hasError && (
          <FieldErrorMessage message={`${form.errors[field.name]}`} />
        )}
      </Box>
    </Box>
  );
};

export const RadioField = ({
  field,
  label,
  value,
  hint,
  form,
  ...rest
}: FieldProps & SharedInputProps): JSX.Element => {
  const hasError = field.name in form.errors;

  return (
    <Box as="label">
      <Box display="flex" alignItems="baseline">
        <Box mr={20}>
          <Radio
            {...field}
            {...rest}
            value={value}
            // I really thought Formik would handle this… :thinking:
            checked={field.value === value}
          />
        </Box>
        {label && (
          <Box mb={2}>
            <Text color="heading" fontWeight={500}>
              {label}
            </Text>
            {hint && <Text variant="sm">{hint}</Text>}
          </Box>
        )}
      </Box>
      {/* Fixed height here stops the input jumping if there's an error */}
      <Box minHeight="20px" paddingTop="5px" mb="6px">
        {hasError && (
          <FieldErrorMessage message={`${form.errors[field.name]}`} />
        )}
      </Box>
    </Box>
  );
};

export const CheckboxGroup = ({
  children,
  fieldName,
  errors,
}: PropsWithChildren<{
  fieldName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FormikErrors<any>;
}>): JSX.Element => {
  const hasError = fieldName in errors;

  const childrenWithProps = Children.map(children, (child) => {
    if (isValidElement(child)) {
      return cloneElement(child, { hideErrors: true, name: fieldName });
    }
    return child;
  });

  return (
    <Box>
      {childrenWithProps}
      <Box minHeight="20px" paddingTop="5px" mb="6px">
        {hasError && (
          <ErrorMessage name={fieldName} component={FormErrorMessage} />
        )}
      </Box>
    </Box>
  );
};
